import React, { FC, useContext, useRef, useState } from 'react';

import { Button, H2, Link, LockIcon, WorldIcon } from '@smartshell/ui-kit';
import cn from 'classnames';

import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';

import { FeatureHOC } from '../FeatureHOC';
import { DisablingCashBox } from '../Modals/components/CashBoxModal/components';
import { EBeneficiaryStatus } from '../Modals/components/TinkoffModal/interfaces';
import { Status } from './components';
import s from './IntegrationCard.module.scss';
import { IIntegrationCardProps } from './interfaces';

export const IntegrationCard: FC<IIntegrationCardProps> = ({
  title,
  description,
  featuresFlag,
  setting,
  link,
  soon = false,
  action,
  status,
  beneficiary,
  isCountry = true,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    state: { settings },
    actions: { setSetting },
  } = useContext(SettingsContext);

  const isAvailable = useIsFeatureAvailable(featuresFlag);

  const [disabledCashBox, setDisabledCashBox] = useState<boolean>(false);
  const refBlock = useRef<HTMLDivElement | null>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  return (
    <>
      {disabledCashBox && (
        <DisablingCashBox onClose={() => setDisabledCashBox(false)} />
      )}
      <div className={s.container} ref={refBlock}>
        <div className={s.header}>
          <H2 className={cn(!isCountry && s.disabled)}>{title}</H2>
          {(!isAvailable || !isCountry) && (
            <>
              <div
                className={s.lock}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {!isCountry ? (
                  <WorldIcon className={s.lock_icon} size="s" color="violet" />
                ) : (
                  <LockIcon className={s.lock_icon} size="s" color="violet" />
                )}
              </div>
              <div
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={cn(s.feature_hoc, isHovered && s.hovered)}
              >
                <FeatureHOC isCountry={!isCountry} />
              </div>
            </>
          )}
          <Status
            status={status}
            setting={setting}
            isAvailable={isAvailable}
            featuresFlag={featuresFlag}
          />
        </div>
        <p className={cn(s.text, !isCountry && s.disabled)}>{description}</p>
        {link && <Link title={t('more')} link={link} className={s.link} />}
        {isCountry && (
          <div className={s.footer}>
            {soon ? (
              <span className={s.soon}>{t('soon')}</span>
            ) : (
              <Button
                onClick={() => {
                  if (setting === 'isCashboxEnabled') {
                    if (!isAvailable && settings[setting]) {
                      setDisabledCashBox(true);
                      return;
                    }
                  }

                  if (
                    setting === 'tinkoffSbpEnabled' &&
                    !settings[setting] &&
                    beneficiary
                  ) {
                    setSetting({
                      alias: ESettingsAliases.TINKOFF_SBP_ENABLED,
                      value: '1',
                    });
                  } else if (status === EBeneficiaryStatus.ERROR) {
                    setSetting({
                      alias: ESettingsAliases.TINKOFF_SBP_ENABLED,
                      value: '0',
                    });
                    return;
                  }

                  action?.();
                }}
                fullWidth
                className={s.button}
                variant="secondary"
                disabled={
                  !isAvailable && setting && settings[setting] === true
                    ? false
                    : !isAvailable
                }
              >
                {(() => {
                  const isCashboxDeactivation =
                    setting === 'isCashboxEnabled' &&
                    !isAvailable &&
                    setting &&
                    Boolean(settings[setting]);

                  const isErrorStatus =
                    status === EBeneficiaryStatus.ERROR &&
                    setting &&
                    Boolean(settings[setting]);

                  const isTelegramChannel =
                    setting === 'telegramChannelId' &&
                    Boolean(settings?.telegramChannelId?.id);

                  const isOtherSetting =
                    setting &&
                    Boolean(settings[setting]) &&
                    setting !== 'telegramChannelId';

                  if (isCashboxDeactivation || isErrorStatus) {
                    return t('deactivate');
                  } else if (isTelegramChannel) {
                    return t('nav.settings');
                  } else if (isOtherSetting) {
                    return t('nav.settings');
                  } else {
                    return t('hook');
                  }
                })()}
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
